import React, { Suspense, lazy } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ImpersonationBanner from './components/layout/ImpersonationBanner';
import ToSReacceptanceModal from './components/ToSReacceptanceModal';
import OrgPickerSheet from './components/OrgPickerSheet';
import { PushNotificationProvider } from './contexts/PushNotificationContext';
import { BillingProvider } from './contexts/BillingContext';
import { FeaturesProvider, useFeatures } from './contexts/FeaturesContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { InstallPrompt } from './components/pwa';
import { AgeVerificationModal, CookieConsentBanner, ErrorBoundary, NotificationNavigationListener, OfflineIndicator, Skeleton, SkeletonText } from './components';
import { isNativeApp } from './utils/pwa';
import { checkAndRecoverFilePickerState, installGlobalFilePickerProtection } from './utils/pwaFilePickerRecovery';
import { Capacitor } from '@capacitor/core';
import { getAppLockService } from './services/AppLockService';
import { registerDeepLinkHandler } from './services/deepLinkHandler';
import AppLockOverlay from './components/AppLockOverlay';
import RecoverySheet from './components/RecoverySheet';
import { useRequireOnboarding } from './hooks/useRequireOnboarding';

// Core pages - loaded immediately
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';

// RegisterPage lazy-loaded — only needed during sign-up
const RegisterPage = lazy(() => import('./pages/register'));

// Lazy-loaded pages for code splitting
const TasksPage = lazy(() => import('./pages/TasksPage'));
const BDSMFeaturesPage = lazy(() => import('./pages/BDSMFeaturesPage'));
const BDSMFeaturesDetailPage = lazy(() => import('./pages/BDSMFeaturesDetailPage'));
const ComparisonPage = lazy(() => import('./pages/ComparisonPage'));
const MigrationGuidePage = lazy(() => import('./pages/MigrationGuidePage'));
const OutgrownPage = lazy(() => import('./pages/outgrown'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const ForgotPasswordPage = lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const AdminDashboardPage = lazy(() => import('./pages/admin-dashboard'));
const AdminAuditLogsPage = lazy(() => import('./pages/AdminAuditLogsPage'));
const AdminAuthAuditLogPage = lazy(() => import('./pages/AdminAuthAuditLogPage'));
const AdminUserDetailPage = lazy(() => import('./pages/admin-user-detail'));
const AdminTrustLevelsPage = lazy(() => import('./pages/admin-trust-levels'));
const AdminStoragePage = lazy(() => import('./pages/admin-storage'));
const AdminInvitationsPage = lazy(() => import('./pages/AdminInvitationsPage'));
const AdminOrganizationsPage = lazy(() => import('./pages/admin-organizations'));
const AdminInventoryTypesPage = lazy(() => import('./pages/AdminInventoryTypesPage'));
const AdminOrganizationCategoriesPage = lazy(() => import('./pages/AdminOrganizationCategoriesPage'));
const ChannelTagRequestsPage = lazy(() => import('./pages/ChannelTagRequestsPage'));
const AcceptInvitationPage = lazy(() => import('./pages/AcceptInvitationPage'));
const ModerationQueuePage = lazy(() => import('./pages/ModerationQueuePage'));
const AdminMessageReportsPage = lazy(() => import('./pages/AdminMessageReportsPage'));
const AdminAIModerationPage = lazy(() => import('./pages/ai-moderation'));
const SuperAdminMetricsPage = lazy(() => import('./pages/super-admin-metrics'));
const MonitoringDashboardPage = lazy(() => import('./pages/admin-monitoring'));
const PlatformConfigPage = lazy(() => import('./pages/platform-config'));
const TaskDetailPage = lazy(() => import('./pages/TaskDetailPage'));
const TaskCompletionsPage = lazy(() => import('./pages/TaskCompletionsPage'));
const TaskAuditLogPage = lazy(() => import('./pages/TaskAuditLogPage'));
const CreateTaskPage = lazy(() => import('./pages/CreateTaskPage'));
const ImportTasksPage = lazy(() => import('./pages/ImportTasksPage'));
const OrganizationsPage = lazy(() => import('./pages/organizations'));
const OrganizationSettingsPage = lazy(() => import('./pages/OrganizationSettingsPage'));

// Settings tab components (lazy loaded)
const SettingsGeneralTab = lazy(() => import('./pages/organization-settings/GeneralTab'));
const SettingsMembersTab = lazy(() => import('./pages/organization-settings/MembersTab'));
const SettingsFeaturesTab = lazy(() => import('./pages/organization-settings/FeaturesTab'));
const SettingsDynamicTab = lazy(() => import('./pages/organization-settings/DynamicTab'));
const SettingsSafetyTab = lazy(() => import('./pages/organization-settings/SafetyTab'));
const SettingsBillingTab = lazy(() => import('./pages/organization-settings/BillingTab'));
const SettingsAdvancedTab = lazy(() => import('./pages/organization-settings/AdvancedTab'));
const OrgSettingsRootTab = lazy(() => import('./pages/organization-settings/tabs/RootTab'));
// Spec 031: InboxPage deprecated - route now redirects to /helm
const AccountSettingsPage = lazy(() => import('./pages/AccountSettingsPage'));

// Account settings tab components (lazy loaded)
const AccountSettingsRootTab = lazy(() => import('./pages/account-settings/tabs/RootTab'));
const AccountProfileTab = lazy(() => import('./pages/account-settings/tabs/ProfileTab'));
const AccountPrivacyTab = lazy(() => import('./pages/account-settings/tabs/PrivacyTab'));
const AccountNotificationsTab = lazy(() => import('./pages/account-settings/tabs/NotificationsTab'));
const AccountSubscriptionTab = lazy(() => import('./pages/account-settings/tabs/SubscriptionTab'));
const AccountQuickActionsTab = lazy(() => import('./pages/account-settings/tabs/QuickActionsTab'));
const AccountSafetyTab = lazy(() => import('./pages/account-settings/tabs/SafetyTab'));
const AccountAboutTab = lazy(() => import('./pages/account-settings/tabs/AboutTab'));
const BillingPage = lazy(() => import('./pages/BillingPage'));
const BillingSuccessPage = lazy(() => import('./pages/BillingSuccessPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const PrivacyPolicyPage = lazy(() => import('./pages/privacy-policy'));
const TermsOfServicePage = lazy(() => import('./pages/terms-of-service'));
const SmsTermsPage = lazy(() => import('./pages/sms-terms'));
const SupportPage = lazy(() => import('./pages/support'));
const DataDeletionPage = lazy(() => import('./pages/DataDeletionPage'));
const NewsletterConfirmedPage = lazy(() => import('./pages/NewsletterConfirmedPage'));
const NewsletterUnsubscribePage = lazy(() => import('./pages/NewsletterConfirmedPage').then(m => ({ default: m.NewsletterUnsubscribePage })));
const MediaLibraryPage = lazy(() => import('./pages/media-library'));
const MediaBillingPage = lazy(() => import('./pages/MediaBillingPage'));
const MediaCheckoutPage = lazy(() => import('./pages/MediaCheckoutPage'));
const JournalPage = lazy(() => import('./pages/JournalPage'));
const JournalNewPage = lazy(() => import('./pages/journal/JournalNewPage'));
const JournalViewPage = lazy(() => import('./pages/journal/JournalViewPage'));
const JournalEditPage = lazy(() => import('./pages/journal/JournalEditPage'));
const StreaksPage = lazy(() => import('./pages/StreaksPage'));
const PerformanceDashboardPage = lazy(() => import('./pages/PerformanceDashboardPage'));
const SceneLogPage = lazy(() => import('./pages/scene-log'));
const PointsPage = lazy(() => import('./pages/points'));
const SessionsPage = lazy(() => import('./pages/SessionsPage'));
const TrainingProgramsPage = lazy(() => import('./pages/training-programs'));
const TrainingProgramDetailPage = lazy(() => import('./pages/training-programs/TrainingProgramDetailPage'));
const ChecklistPage = lazy(() => import('./pages/checklist'));
const SafewordPage = lazy(() => import('./pages/safeword'));
const AftercareCheckinPage = lazy(() => import('./pages/safeword/AftercareCheckinPage'));
const OrgasmBankPage = lazy(() => import('./pages/orgasm-bank'));
const RitualsPage = lazy(() => import('./pages/rituals'));
const ContractsPage = lazy(() => import('./pages/contracts'));
const ProtocolsPage = lazy(() => import('./pages/protocols'));
const CreateProtocolPage = lazy(() => import('./pages/CreateProtocolPage'));
const RelationshipReviewsPage = lazy(() => import('./pages/relationship-reviews'));
const ChastityPage = lazy(() => import('./pages/ChastityPage'));
const RewardsPage = lazy(() => import('./pages/RewardsPage'));
const TaskTemplatesPage = lazy(() => import('./pages/task-templates'));
const PraiseInbox = lazy(() => import('./pages/PraiseInbox'));
const SafetyFirstPage = lazy(() => import('./pages/safety-first'));
const SafetyCheckinPage = lazy(() => import('./pages/safety'));
const PartnerCheckinStatusPage = lazy(() => import('./pages/safety/PartnerCheckinStatusPage'));
const CheckinBuddiesPage = lazy(() => import('./pages/safety/CheckinBuddiesPage'));
const StorageManagementPage = lazy(() => import('./pages/storage-management'));
const UserStorageManagementPage = lazy(() => import('./pages/user-storage-management'));
const InventoryPage = lazy(() => import('./pages/inventory'));
// TimelinePage removed — consolidated into Chronicle tabs
// CollaringPage removed — consolidated into Chronicle tabs
const ChronicleTodayPage = lazy(() => import('./pages/chronicle/ChronicleTodayPage'));
const MilestonesPage = lazy(() => import('./pages/chronicle/MilestonesPage'));
const SharedWithMePage = lazy(() => import('./pages/chronicle/SharedWithMePage'));
const ArchivePage = lazy(() => import('./pages/chronicle/ArchivePage'));
const TravelSchedulePage = lazy(() => import('./pages/travel-schedule'));
// ImpactBodyMapPage removed — consolidated into Command tabs
// ServiceRotationsPage removed (Spec 126) — rotations migrated to tasks
// Command R4 — Structure (Training + Contracts combined) and Store (Sub placeholder)
const StructurePage = lazy(() => import('./pages/command/StructurePage'));
const DomEconomyPanel = lazy(() => import('./pages/command/DomEconomyPanel'));
const StorePage = lazy(() => import('./pages/command/StorePage'));
const ShareTargetPage = lazy(() => import('./pages/ShareTargetPage'));
const HelmPage = lazy(() => import('./pages/HelmPage'));
const JoinOrganizationPage = lazy(() => import('./pages/JoinOrganizationPage'));
const PricingPage = lazy(() => import('./pages/PricingPage'));
const HelpPage = lazy(() => import('./pages/HelpPage'));
const MessagesPage = lazy(() => import('./pages/MessagesPage'));
const AgeVerificationPage = lazy(() => import('./pages/AgeVerificationPage'));
const PendingDeletionPage = lazy(() => import('./pages/PendingDeletionPage'));
const DiditTestPage = lazy(() => import('./pages/DiditTestPage'));
const DiditRegistrationCallbackPage = lazy(() => import('./pages/DiditRegistrationCallbackPage'));
const AppLockRecoveryPage = lazy(() => import('./pages/AppLockRecovery'));

// Community routes
// Blog pages
const BlogListPage = lazy(() => import('./pages/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./pages/blog/BlogPostPage'));

// Admin Marketing
const AdminMarketingPage = lazy(() => import('./pages/admin/AdminMarketingPage'));

// Spec 130: Channel-based Connect page
const ConnectPage = lazy(() => import('./pages/connect'));

// Shared templates page (Connect pillar)
const SharedTemplatesPage = lazy(() => import('./pages/connect/SharedTemplatesPage'));
const MentorshipPage = lazy(() => import('./pages/MentorshipPage'));

// Spec 074: Cultivate (Discovery) pages
const CultivateFeed = lazy(() => import('./pages/cultivate/CultivateFeed'));
const CultivateHierarchies = lazy(() => import('./pages/cultivate/CultivateHierarchies'));
const CultivateMatches = lazy(() => import('./pages/cultivate/CultivateMatches'));
const CultivateMessages = lazy(() => import('./pages/cultivate/CultivateMessages'));
const CultivateChat = lazy(() => import('./pages/cultivate/CultivateChat'));
const CultivateProfile = lazy(() => import('./pages/cultivate/CultivateProfile'));
const CultivateProfileDetail = lazy(() => import('./pages/cultivate/CultivateProfileDetail'));
const CultivateSettings = lazy(() => import('./pages/cultivate/CultivateSettings'));
const CultivateBlockedUsers = lazy(() => import('./pages/cultivate/CultivateBlockedUsers'));

// Cross-Pillar Search (Gap 6)
const SearchPage = lazy(() => import('./pages/SearchPage'));

// Spec 191: Partner Detail view
const PartnerDetailPage = lazy(() => import('./pages/partners/PartnerDetailPage'));

// Feature detail pages
const TasksFeaturePage = lazy(() => import('./pages/features/TasksFeaturePage'));
const ChastityFeaturePage = lazy(() => import('./pages/features/ChastityFeaturePage'));
const ProgressFeaturePage = lazy(() => import('./pages/features/ProgressFeaturePage'));
const ProtocolFeaturePage = lazy(() => import('./pages/features/ProtocolFeaturePage'));
const PersonalFeaturePage = lazy(() => import('./pages/features/PersonalFeaturePage'));

// Loading component
const PageLoader = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    flexDirection: 'column',
    gap: '16px'
  }}>
    <Skeleton variant="rounded" width={200} height={24} />
    <SkeletonText lines={2} />
  </div>
);

// Exported for direct unit testing of the auth-vs-features gating order
// (see src/__tests__/App.routeGuards.test.tsx) — not otherwise consumed outside this file.
export const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, refreshUser } = useAuth();
  const { features, loading: featuresLoading } = useFeatures();
  const [showAgeVerification, setShowAgeVerification] = React.useState(false);

  useRequireOnboarding();

  React.useEffect(() => {
    // Check if user needs age verification (only if feature is enabled)
    if (features.ageVerification && user && user.requiresAgeVerification) {
      setShowAgeVerification(true);
    } else {
      setShowAgeVerification(false);
    }
  }, [user, features.ageVerification]);

  // Auth resolution and the login redirect must never be blocked by a stuck
  // features query (a session-expiry queryClient.clear() can orphan an
  // in-flight features fetch whose observer never settles — see bug-registry.md).
  // Check loading/!user before featuresLoading so an expired session can
  // always reach /login even if features never resolves.
  if (loading) {
    return <div className="loading"><SkeletonText lines={2} /></div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // pending-deletion cancel flow (2026-09-24): a pending-deletion account can
  // sign in, but the ONLY things it can do are cancel the deletion or sign
  // out — every other route 403s server-side (authenticateTokenAllowPendingDeletion).
  // Checked before featuresLoading/age-verification so this screen never waits
  // on a query a pending-deletion user isn't allowed to make.
  if (user.accountStatus === 'pending_deletion') {
    return <PendingDeletionPage />;
  }

  if (featuresLoading) {
    return <div className="loading"><SkeletonText lines={2} /></div>;
  }

  // Show age verification modal if required (only when feature is enabled)
  if (showAgeVerification) {
    return (
      <AgeVerificationModal
        isOpen={true}
        onVerified={() => {
          setShowAgeVerification(false);
          refreshUser();
        }}
      />
    );
  }

  return <>{children}</>;
};

const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading"><SkeletonText lines={2} /></div>;
  }

  return !user ? <>{children}</> : <Navigate to="/tasks" />;
};

// Route that shows features page for public, redirects to helm for authenticated
// In native app: skip marketing page, go straight to login
const FeaturesRoute: React.FC = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading"><SkeletonText lines={2} /></div>;
  }

  if (user) return <Navigate to="/helm" />;
  if (isNativeApp()) return <Navigate to="/login" />;
  return <BDSMFeaturesPage />;
};

// Feature-gated route - redirects to dashboard if feature is disabled
// Exported for direct unit testing (see src/__tests__/App.routeGuards.test.tsx).
export const FeatureRoute: React.FC<{ children: React.ReactNode; feature: 'mediaLibrary' | 'mentorship' | 'dynamicEvolutionArchive' }> = ({ children, feature }) => {
  const { user, loading: authLoading } = useAuth();
  const { features, loading: featuresLoading } = useFeatures();

  // Same ordering fix as PrivateRoute — auth resolution must not be blocked
  // by a stuck features query (see bug-registry.md).
  if (authLoading) {
    return <div className="loading"><SkeletonText lines={2} /></div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (featuresLoading) {
    return <div className="loading"><SkeletonText lines={2} /></div>;
  }

  if (!features[feature]) {
    return <Navigate to="/tasks" replace />;
  }

  return <>{children}</>;
};

const DominantGuideRedirect: React.FC = () => {
  React.useEffect(() => { window.location.href = '/Helm-Dominant-QuickStart.pdf'; }, []);
  return null;
};

// Redirect /protocols/:id → /command/protocols/:id so detail pages stay inside the Command pillar shell.
const ProtocolsIdRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/command/protocols/${id}`} replace />;
};

// Redirect /command/training/:programId and /training/:programId → /command/structure/training/:programId
const TrainingProgramIdRedirect: React.FC = () => {
  const { programId } = useParams<{ programId: string }>();
  return <Navigate to={`/command/structure/training/${programId}`} replace />;
};

// Spec 289: /onboard retired — OnboardingPage (Spec 193's role-fork wizard)
// is deleted, its collection responsibilities absorbed into /register's
// Rhythm/Personalize steps. This route only catches stragglers on an old
// bookmarked/emailed /onboard link (Acceptance Criterion #8: redirect, not
// 404). Rendered inside PrivateRoute exactly as OnboardingPage was, so an
// unauthenticated hit still bounces to /login first. '/onboard' stays in
// ONBOARDING_EXEMPT_PREFIXES (onboardingExemptPaths.ts) so PrivateRoute's
// own useRequireOnboarding() call no-ops on this route instead of racing
// this component's own redirect — this component is the single authority
// for where a straggler on this URL ends up.
const OnboardRedirect: React.FC = () => {
  const { user } = useAuth();
  return user?.onboardingIdentityCompletedAt
    ? <Navigate to="/helm" replace />
    : <Navigate to="/register" replace />;
};

// Recovers navigation state after iOS PWA camera crash kills the WebView.
// Also installs a global listener that protects ALL file inputs automatically.
function FilePickerRecovery() {
  const navigate = useNavigate();
  React.useEffect(() => {
    installGlobalFilePickerProtection();
    checkAndRecoverFilePickerState(navigate);
  }, [navigate]);
  return null;
}

function AppRoutes() {
  return (
    <ErrorBoundary>
    <FilePickerRecovery />
    <Suspense fallback={<PageLoader />}>
      <Routes>
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/register"
        element={<RegisterPage />}
      />
      <Route
        path="/register/didit-callback"
        element={<DiditRegistrationCallbackPage />}
      />
      {/* Spec 289: /onboard retired — redirects stragglers, see OnboardRedirect above */}
      <Route
        path="/onboard"
        element={
          <PrivateRoute>
            <OnboardRedirect />
          </PrivateRoute>
        }
      />
      <Route
        path="/verify-email/:token"
        element={<VerifyEmailPage />}
      />
      <Route
        path="/forgot-password"
        element={<ForgotPasswordPage />}
      />
      <Route
        path="/reset-password/:token"
        element={<ResetPasswordPage />}
      />
      {/* Spec 178/181: App Lock Recovery — public routes */}
      <Route
        path="/account/app-lock-recovery"
        element={<AppLockRecoveryPage />}
      />
      <Route
        path="/account/app-lock-recovery/complete"
        element={<AppLockRecoveryPage />}
      />
      <Route
        path="/age-verification"
        element={
          <PrivateRoute>
            <AgeVerificationPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <PrivateRoute>
            <SuperAdminMetricsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/dashboard"
        element={
          <PrivateRoute>
            <AdminDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users"
        element={
          <PrivateRoute>
            <AdminDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/audit-logs"
        element={
          <PrivateRoute>
            <AdminAuditLogsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/auth-audit-log"
        element={
          <PrivateRoute>
            <AdminAuthAuditLogPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/users/:id"
        element={
          <PrivateRoute>
            <AdminUserDetailPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/trust-levels"
        element={
          <PrivateRoute>
            <AdminTrustLevelsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/storage"
        element={
          <PrivateRoute>
            <AdminStoragePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/monitoring"
        element={
          <PrivateRoute>
            <MonitoringDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/moderation"
        element={
          <PrivateRoute>
            <ModerationQueuePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/dm-reports"
        element={
          <PrivateRoute>
            <AdminMessageReportsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/ai-moderation"
        element={
          <PrivateRoute>
            <AdminAIModerationPage />
          </PrivateRoute>
        }
      />
      {/* C6 (2026-06-07 audit): /admin/metrics duplicated /admin (same SuperAdminMetricsPage) — redirect */}
      <Route
        path="/admin/metrics"
        element={<Navigate to="/admin" replace />}
      />
      {/* C4 (2026-06-07 audit): FeatureFlagsPage is @deprecated (superseded by PlatformConfigPage,
          the "Config" admin tab) — redirect the stranded URL to its replacement. */}
      <Route
        path="/admin/feature-flags"
        element={<Navigate to="/admin/platform-config" replace />}
      />
      <Route
        path="/admin/platform-config"
        element={
          <PrivateRoute>
            <PlatformConfigPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/invitations"
        element={
          <PrivateRoute>
            <AdminInvitationsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/organizations"
        element={
          <PrivateRoute>
            <AdminOrganizationsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/inventory-types"
        element={
          <PrivateRoute>
            <AdminInventoryTypesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/organization-categories"
        element={
          <PrivateRoute>
            <AdminOrganizationCategoriesPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/tag-category-requests"
        element={
          <PrivateRoute>
            <ChannelTagRequestsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/admin/marketing"
        element={
          <PrivateRoute>
            <AdminMarketingPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/accept-invitation"
        element={<AcceptInvitationPage />}
      />
      <Route
        path="/join/:inviteCode"
        element={<JoinOrganizationPage />}
      />
      <Route
        path="/"
        element={<FeaturesRoute />}
      />
      <Route
        path="/archive"
        element={<LandingPage />}
      />
      <Route
        path="/bdsm"
        element={<Navigate to="/" replace />}
      />
      <Route
        path="/features"
        element={<BDSMFeaturesDetailPage />}
      />
      <Route path="/features/tasks" element={<TasksFeaturePage />} />
      <Route path="/features/chastity" element={<ChastityFeaturePage />} />
      <Route path="/features/progress" element={<ProgressFeaturePage />} />
      <Route path="/features/protocol" element={<ProtocolFeaturePage />} />
      <Route path="/features/personal" element={<PersonalFeaturePage />} />
      {/* Primary tasks route */}
      <Route
        path="/tasks"
        element={
          <PrivateRoute>
            <TasksPage />
          </PrivateRoute>
        }
      />
      {/* Legacy dashboard route - redirect to /tasks */}
      <Route
        path="/dashboard"
        element={<Navigate to="/tasks" replace />}
      />
      <Route
        path="/helm"
        element={
          <PrivateRoute>
            <HelmPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks/create"
        element={
          <PrivateRoute>
            <CreateTaskPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks/import"
        element={
          <PrivateRoute>
            <ImportTasksPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks/:id"
        element={
          <PrivateRoute>
            <TaskDetailPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks/:id/completions"
        element={
          <PrivateRoute>
            <TaskCompletionsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/tasks/:id/audit-log"
        element={
          <PrivateRoute>
            <TaskAuditLogPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/share-target"
        element={
          <PrivateRoute>
            <ShareTargetPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/organizations"
        element={
          <PrivateRoute>
            <OrganizationsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/organizations/settings"
        element={
          <PrivateRoute>
            <OrganizationSettingsPage />
          </PrivateRoute>
        }
      >
        <Route index element={<OrgSettingsRootTab />} />
        <Route path="general" element={<SettingsGeneralTab />} />
        <Route path="members" element={<SettingsMembersTab />} />
        <Route path="features" element={<SettingsFeaturesTab />} />
        <Route path="dynamic" element={<SettingsDynamicTab />} />
        <Route path="safety" element={<SettingsSafetyTab />} />
        <Route path="billing" element={<SettingsBillingTab />} />
        <Route path="advanced" element={<SettingsAdvancedTab />} />
      </Route>
      <Route
        path="/task-templates"
        element={
          <PrivateRoute>
            <TaskTemplatesPage />
          </PrivateRoute>
        }
      />
      {/* Spec 031: Inbox deprecated - redirect to Helm */}
      <Route
        path="/inbox"
        element={<Navigate to="/helm" replace />}
      />
      <Route
        path="/notifications"
        element={<Navigate to="/helm" replace />}
      />
      <Route
        path="/settings/storage"
        element={
          <PrivateRoute>
            <StorageManagementPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings/media-storage"
        element={
          <PrivateRoute>
            <UserStorageManagementPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <PrivateRoute>
            <AccountSettingsPage />
          </PrivateRoute>
        }
      >
        <Route index element={<AccountSettingsRootTab />} />
        <Route path="profile" element={<AccountProfileTab />} />
        <Route path="privacy" element={<AccountPrivacyTab />} />
        <Route path="notifications" element={<AccountNotificationsTab />} />
        <Route path="account" element={<AccountSubscriptionTab />} />
        <Route path="quick-actions" element={<AccountQuickActionsTab />} />
        <Route path="safety" element={<AccountSafetyTab />} />
        <Route path="about" element={<AccountAboutTab />} />
      </Route>
      <Route
        path="/sessions"
        element={
          <PrivateRoute>
            <SessionsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing"
        element={
          <PrivateRoute>
            <BillingPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/success"
        element={
          <PrivateRoute>
            <BillingSuccessPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/checkout"
        element={
          <PrivateRoute>
            <CheckoutPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/library"
        element={
          <FeatureRoute feature="mediaLibrary">
            <MediaLibraryPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/library/billing"
        element={
          <FeatureRoute feature="mediaLibrary">
            <MediaBillingPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/library/billing/checkout"
        element={
          <FeatureRoute feature="mediaLibrary">
            <MediaCheckoutPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/journal"
        element={
          <FeatureRoute feature="mediaLibrary">
            <JournalPage />
          </FeatureRoute>
        }
      />
      <Route path="/journal/new" element={<FeatureRoute feature="mediaLibrary"><JournalNewPage /></FeatureRoute>} />
      <Route path="/journal/:entryId" element={<FeatureRoute feature="mediaLibrary"><JournalViewPage /></FeatureRoute>} />
      <Route path="/journal/:entryId/edit" element={<FeatureRoute feature="mediaLibrary"><JournalEditPage /></FeatureRoute>} />
      <Route
        path="/streaks"
        element={
          <PrivateRoute>
            <StreaksPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/performance"
        element={
          <PrivateRoute>
            <PerformanceDashboardPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/mood"
        element={<Navigate to="/chronicle" replace />}
      />
      <Route
        path="/scenes"
        element={
          <FeatureRoute feature="mediaLibrary">
            <SceneLogPage />
          </FeatureRoute>
        }
      />
      <Route
        path="/checklist"
        element={
          <PrivateRoute>
            <ChecklistPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/points"
        element={
          <PrivateRoute>
            <PointsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/training"
        element={
          <PrivateRoute>
            <TrainingProgramsPage />
          </PrivateRoute>
        }
      />
      {/* 0zzz-fs: task views linked here before it was routed; keep old links and cached builds working. */}
      <Route path="/training-programs" element={<Navigate to="/training" replace />} />
      {/* Also 0zzz-fs: web/e2e/training-programs.spec.ts navigates here. */}
      <Route path="/training/:programId" element={<TrainingProgramIdRedirect />} />
      <Route
        path="/safeword"
        element={
          <PrivateRoute>
            <SafewordPage />
          </PrivateRoute>
        }
      />
      {/* bug-registry 0zzz-au: dedicated full-screen aftercare check-in route, reached
          automatically after a hard-limit trigger (useSafeword.ts) or via the resumable
          "Continue check-in" card. Deliberately not a Sheet/Modal — see AftercareCheckinPage.tsx. */}
      <Route
        path="/safeword/aftercare/:logId"
        element={
          <PrivateRoute>
            <AftercareCheckinPage />
          </PrivateRoute>
        }
      />
      {/* Spec 169: Consequences standalone page removed — redirect to Protocols */}
      <Route path="/consequences" element={<Navigate to="/command/protocols" replace />} />
      <Route path="/orgasm-bank" element={<Navigate to="/command/orgasm-bank" replace />} />
      <Route
        path="/rituals"
        element={
          <PrivateRoute>
            <RitualsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <PrivateRoute>
            <ContractsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/contracts/:id"
        element={
          <PrivateRoute>
            <ContractsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/protocols"
        element={<Navigate to="/command/protocols" replace />}
      />
      <Route
        path="/protocols/create"
        element={<Navigate to="/command/protocols/create" replace />}
      />
      <Route
        path="/protocols/:id"
        element={<ProtocolsIdRedirect />}
      />
      <Route
        path="/relationship-reviews"
        element={
          <PrivateRoute>
            <RelationshipReviewsPage />
          </PrivateRoute>
        }
      />
      <Route path="/chastity" element={<Navigate to="/command/chastity" replace />} />
      <Route path="/rewards" element={<Navigate to="/command/rewards" replace />} />
      <Route path="/praise" element={<Navigate to="/command/praise" replace />} />
      <Route
        path="/inventory"
        element={
          <PrivateRoute>
            <InventoryPage />
          </PrivateRoute>
        }
      />
      <Route path="/travel-schedules" element={<Navigate to="/command/travel" replace />} />
      {/* Public Blog */}
      <Route path="/blog" element={<BlogListPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />

      {/* Public Legal Pages */}
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms-of-service" element={<TermsOfServicePage />} />
      <Route path="/sms-terms" element={<SmsTermsPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/data-deletion" element={<DataDeletionPage />} />
      <Route path="/newsletter/confirmed" element={<NewsletterConfirmedPage />} />
      <Route path="/newsletter/unsubscribe" element={<NewsletterUnsubscribePage />} />
      <Route path="/bdsm/features" element={<Navigate to="/features" replace />} />
      <Route path="/bdsm/comparison" element={<ComparisonPage />} />
      <Route path="/bdsm/migration" element={<MigrationGuidePage />} />
      <Route path="/bdsm/outgrown" element={<OutgrownPage />} />
      <Route path="/safety" element={<SafetyFirstPage />} />
      <Route path="/safety/check-in" element={<PrivateRoute><SafetyCheckinPage /></PrivateRoute>} />
      {/* Spec 265 punch list: partner check-in status view (Dominant reached via Level-2 escalation push).
          Coexists with the static /safety/check-in above — React Router v6 ranks the static route higher,
          so /safety/check-in still resolves to the self page (Spec 260 precedent). */}
      <Route path="/safety/check-in/:checkinId" element={<PrivateRoute><PartnerCheckinStatusPage /></PrivateRoute>} />
      {/* Spec 314 Phase A — buddy list/designate page, gated by features.checkinBuddyNetwork
          inside the page itself (same pattern as SafetyCheckinPage's emergencyTimer gate). */}
      <Route path="/safety/checkin-buddies" element={<PrivateRoute><CheckinBuddiesPage /></PrivateRoute>} />
      <Route path="/dominant" element={<DominantGuideRedirect />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/help" element={<HelpPage />} />
      <Route path="/didit-test" element={<DiditTestPage />} />
      {/* Centralized Messages */}
      <Route
        path="/messages"
        element={
          <PrivateRoute>
            <MessagesPage />
          </PrivateRoute>
        }
      />
      {/* Spec 260: literal route, ranked above the dynamic :conversationId
          param below by React Router v6 — no ambiguity with a real id. */}
      <Route
        path="/messages/new"
        element={
          <PrivateRoute>
            <MessagesPage openComposeOnMount />
          </PrivateRoute>
        }
      />
      <Route
        path="/messages/:conversationId"
        element={
          <PrivateRoute>
            <MessagesPage />
          </PrivateRoute>
        }
      />
      {/* ═══ Spec 074: Four-Pillar Routes ═══ */}

      {/* Cultivate (Discovery) */}
      <Route path="/cultivate" element={<PrivateRoute><CultivateFeed /></PrivateRoute>} />
      <Route path="/cultivate/hierarchies" element={<PrivateRoute><CultivateHierarchies /></PrivateRoute>} />
      <Route path="/cultivate/matches" element={<PrivateRoute><CultivateMatches /></PrivateRoute>} />
      <Route path="/cultivate/messages" element={<PrivateRoute><CultivateMessages /></PrivateRoute>} />
      <Route path="/cultivate/messages/:id" element={<PrivateRoute><CultivateChat /></PrivateRoute>} />
      <Route path="/cultivate/profile" element={<PrivateRoute><CultivateProfile /></PrivateRoute>} />
      <Route path="/cultivate/profile/:userId" element={<PrivateRoute><CultivateProfileDetail /></PrivateRoute>} />
      <Route path="/cultivate/settings" element={<PrivateRoute><CultivateSettings /></PrivateRoute>} />
      <Route path="/cultivate/settings/blocked" element={<PrivateRoute><CultivateBlockedUsers /></PrivateRoute>} />

      {/* Connect — Spec 130: Channel-based community */}
      <Route path="/connect" element={<PrivateRoute><ConnectPage /></PrivateRoute>} />
      <Route path="/connect/shared" element={<PrivateRoute><SharedTemplatesPage /></PrivateRoute>} />
      <Route path="/connect/mentorship" element={<PrivateRoute><FeatureRoute feature="mentorship"><MentorshipPage /></FeatureRoute></PrivateRoute>} />
      <Route path="/connect/messages" element={<PrivateRoute><MessagesPage /></PrivateRoute>} />
      <Route path="/connect/praise" element={<Navigate to="/connect" replace />} />

      {/* Spec 191: Partner Detail view */}
      <Route path="/partners/:id" element={<PrivateRoute><PartnerDetailPage /></PrivateRoute>} />

      {/* Command — maps to existing pages at new URLs */}
      <Route path="/command" element={<PrivateRoute><TasksPage /></PrivateRoute>} />
      <Route path="/command/tasks" element={<Navigate to="/command" replace />} />
      {/* Command R4 — Structure (Training + Contracts combined) */}
      <Route path="/command/structure" element={<PrivateRoute><StructurePage /></PrivateRoute>} />
      <Route path="/command/structure/:section" element={<PrivateRoute><StructurePage /></PrivateRoute>} />
      {/* Legacy standalone structure sub-pages — redirect into nested structure routes */}
      <Route path="/command/contracts" element={<Navigate to="/command/structure/contracts" replace />} />
      <Route path="/command/training" element={<Navigate to="/command/structure/training" replace />} />
      <Route path="/command/autonomy" element={<Navigate to="/command/structure/autonomy" replace />} />
      {/* Command R4 — Sub Store placeholder (Phase 3) */}
      <Route path="/command/store" element={<PrivateRoute><StorePage /></PrivateRoute>} />
      <Route path="/command/training/:programId" element={<TrainingProgramIdRedirect />} />
      <Route path="/command/structure/training/:programId" element={<PrivateRoute><TrainingProgramDetailPage /></PrivateRoute>} />
      <Route path="/command/protocols" element={<PrivateRoute><ProtocolsPage /></PrivateRoute>} />
      <Route path="/command/protocols/create" element={<PrivateRoute><CreateProtocolPage /></PrivateRoute>} />
      <Route path="/command/protocols/:id" element={<PrivateRoute><ProtocolsPage /></PrivateRoute>} />
      <Route path="/command/progress" element={<PrivateRoute><StreaksPage /></PrivateRoute>} />
      <Route path="/command/praise" element={<PrivateRoute><PraiseInbox /></PrivateRoute>} />
      <Route path="/command/points" element={<PrivateRoute><PointsPage /></PrivateRoute>} />
      {/* Spec 169: Consequences standalone tab removed — redirect to Protocols */}
      <Route path="/command/consequences" element={<Navigate to="/command/protocols" replace />} />
      <Route path="/command/rewards" element={<PrivateRoute><RewardsPage /></PrivateRoute>} />
      <Route path="/command/orgasm-bank" element={<PrivateRoute><OrgasmBankPage /></PrivateRoute>} />
      <Route path="/command/economy" element={<PrivateRoute><Suspense fallback={<div />}><DomEconomyPanel standalone /></Suspense></PrivateRoute>} />
      {/* Economy sub-pages — nested under /command/economy/ so Economy tab stays active */}
      <Route path="/command/economy/praise" element={<PrivateRoute><PraiseInbox /></PrivateRoute>} />
      <Route path="/command/economy/points" element={<PrivateRoute><PointsPage /></PrivateRoute>} />
      <Route path="/command/economy/rewards" element={<PrivateRoute><RewardsPage /></PrivateRoute>} />
      <Route path="/command/economy/orgasm-bank" element={<PrivateRoute><OrgasmBankPage /></PrivateRoute>} />
      <Route path="/command/chastity" element={<PrivateRoute><ChastityPage /></PrivateRoute>} />
      {/* Travel Schedules — standalone page */}
      <Route path="/command/travel" element={<PrivateRoute><TravelSchedulePage /></PrivateRoute>} />
      <Route path="/command/reviews" element={<PrivateRoute><RelationshipReviewsPage /></PrivateRoute>} />
      <Route path="/command/rotations" element={<Navigate to="/command" replace />} />

      {/* Chronicle — consolidated from 11 tabs to 8 */}
      <Route path="/chronicle" element={<PrivateRoute><ChronicleTodayPage /></PrivateRoute>} />
      <Route path="/chronicle/journal" element={<FeatureRoute feature="mediaLibrary"><JournalPage /></FeatureRoute>} />
      <Route path="/chronicle/journal/new" element={<FeatureRoute feature="mediaLibrary"><JournalNewPage /></FeatureRoute>} />
      <Route path="/chronicle/journal/:entryId" element={<FeatureRoute feature="mediaLibrary"><JournalViewPage /></FeatureRoute>} />
      <Route path="/chronicle/journal/:entryId/edit" element={<FeatureRoute feature="mediaLibrary"><JournalEditPage /></FeatureRoute>} />
      <Route path="/chronicle/scenes" element={<FeatureRoute feature="mediaLibrary"><SceneLogPage /></FeatureRoute>} />
      <Route path="/chronicle/boundaries" element={<PrivateRoute><ChecklistPage /></PrivateRoute>} />
      <Route path="/chronicle/milestones" element={<PrivateRoute><MilestonesPage /></PrivateRoute>} />
      <Route path="/chronicle/library" element={<FeatureRoute feature="mediaLibrary"><MediaLibraryPage /></FeatureRoute>} />
      <Route path="/chronicle/inventory" element={<PrivateRoute><InventoryPage /></PrivateRoute>} />
      <Route path="/chronicle/shared" element={<PrivateRoute><SharedWithMePage /></PrivateRoute>} />
      <Route path="/chronicle/archive" element={<PrivateRoute><FeatureRoute feature="dynamicEvolutionArchive"><ArchivePage /></FeatureRoute></PrivateRoute>} />

      {/* Chronicle backward-compat redirects (removed tabs) */}
      <Route path="/chronicle/checkin" element={<Navigate to="/chronicle" replace />} />
      <Route path="/chronicle/mood" element={<Navigate to="/chronicle" replace />} />
      <Route path="/chronicle/safety" element={<Navigate to="/chronicle" replace />} />
      <Route path="/chronicle/timeline" element={<Navigate to="/chronicle/milestones" replace />} />
      <Route path="/chronicle/collaring" element={<Navigate to="/chronicle/milestones" replace />} />
      <Route path="/chronicle/body-map" element={<Navigate to="/chronicle/scenes" replace />} />

      {/* Cross-Pillar Search (Gap 6) */}
      <Route path="/search" element={<PrivateRoute><SearchPage /></PrivateRoute>} />

      {/* 404 catch-all — must be last */}
      <Route path="*" element={<NotFoundPage />} />

      </Routes>
    </Suspense>
    </ErrorBoundary>
  );
}

// Spec 178/181: Initialize app lock + deep links on native platform (flag-gated)
function AppLockBootstrap() {
  const { features } = useFeatures();
  const enabled = Capacitor.isNativePlatform() && features.biometricAppLock;

  React.useEffect(() => {
    if (enabled) {
      getAppLockService().init();
      registerDeepLinkHandler();
    }
  }, [enabled]);

  if (!enabled) return null;
  return (
    <>
      <AppLockOverlay />
      <RecoverySheet />
    </>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ErrorBoundary>
          <FeaturesProvider>
            <AuthProvider>
              <OrganizationProvider>
                <BillingProvider>
                  <PushNotificationProvider>
                    {/* Rendered here (not inside PillarLayout) so an impersonation
                        session is never invisible on a route PillarLayout doesn't
                        wrap — e.g. a freshly-registered impersonation target who
                        lands on the standalone /register onboarding flow before
                        ever reaching the main app shell. Found via a real E2E
                        failure: the banner's own "if (!impersonatedBy) return null"
                        guard already makes it a no-op outside impersonation, so
                        rendering it globally has no effect on normal sessions. */}
                    <ImpersonationBanner />
                    {/* Spec 327: app root (was inside PillarLayout) so server-side
                        ToS enforcement can always surface the modal — including
                        on /organizations, onboarding and other standalone routes.
                        Renders nothing without a logged-in user. */}
                    <ToSReacceptanceModal />
                    {/* 0zzz-fe: app root (needs useOrganization() + react-router's
                        useNavigate()) so a stale/ambiguous org write can be
                        resolved in place instead of hard-redirecting and
                        discarding in-progress input. Renders nothing until the
                        client dispatches ORG_PICKER_REQUIRED_EVENT. */}
                    <OrgPickerSheet />
                    <AppRoutes />
                    <NotificationNavigationListener />
                    <OfflineIndicator />
                    <InstallPrompt />
                    <CookieConsentBanner />
                    <ToastContainer
                      position="bottom-center"
                      autoClose={4000}
                      hideProgressBar={false}
                      newestOnTop={false}
                      closeOnClick
                      pauseOnFocusLoss
                      draggable
                      pauseOnHover
                      theme="dark"
                      toastStyle={{
                        background: 'var(--theme-toast-bg)',
                        color: 'var(--theme-toast-text)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '12px',
                        fontSize: '14px',
                      }}
                      style={{
                        bottom: 'calc(72px + env(safe-area-inset-bottom))',
                        width: 'min(100vw - 32px, 380px)',
                        left: '50%',
                        transform: 'translateX(-50%)',
                      }}
                    />
                    {/* Spec 178/181: Native-only app lock UI — rendered inside AppLockBootstrap */}
                    <AppLockBootstrap />
                  </PushNotificationProvider>
                </BillingProvider>
              </OrganizationProvider>
            </AuthProvider>
          </FeaturesProvider>
        </ErrorBoundary>
      </BrowserRouter>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
